import React from 'react'
import clsx from 'clsx'
import { ClickAwayListener, Popper } from '@mui/material'
import { useDrop } from 'react-dnd'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import TaskDueDate from '@SonarCX/components/icons/TaskDueDate'
import Tooltip from '../../../../atoms/Tooltip'
import MediaAvatar from '../../../../atoms/MediaAvatar'
import Calendar from '../../../../atoms/Calendar'
import DownArrowSmallIcon from '../../../../icons/DownArrowSmallIcon'
import AsigneeListRow from '../AsigneeListRow'
import DropdownV2 from '../../../../atoms/DropdownV2'
import AddAsigneeModal from '../AddAsigneeModal'
import EmailDrop from '@SonarCX/components/molecules/EmailHolder/EmailDrop'

import { formatTaskForUpload } from './helpers'
import { uploadFileTask } from '@SonarCX/lib/api'
import { EMAIL_DRAG_TYPE } from '@SonarCX/components/molecules/EmailHolder/Email'
import { formatDateRelative, uniqueArray } from '@SonarCX/utilities'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { selectExtensionAPI } from '@SonarCX/slices/extensionAPI'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import { useTaskList } from '@SonarCX/providers/TaskListProvider'
import { useOwner } from '@SonarCX/providers/OwnerProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import useStyles from './styles'

const formatTaskForModal = task => ({
  ...task,
  date: task.due_date,
  section: task.category_id,
  client: task.concerning_user_id,
  primary: task.assignee_ids || [],
  watchers: task.watcher_ids,
  type: task.type_id,
  status: task.status_id,
  priority: task.priority_id,
  tags: task.tags ?? [],
  content: task.details,
  owner_id: task.owner_id
})

const TaskAssignee = ({
  userId,
  index,
  classes,
  numberOfAssignees,
  allUsers,
  loadUser
}) => {
  const user = allUsers[userId]

  React.useEffect(() => {
    if (!user) {
      loadUser(userId)
    }
  }, [user, loadUser, userId])

  return (
    <Tooltip title={user?.name ?? ''} arrow>
      <div>
        <MediaAvatar
          user={user}
          className={clsx(
            classes.avatar,
            index > 0 ? classes.marginLeft : '',
            index === numberOfAssignees - 1 ? '' : classes.avatarBorder
          )}
          style={{
            zIndex: numberOfAssignees - index
          }}
        />
      </div>
    </Tooltip>
  )
}

const Task = ({
  task,
  setModalOpen,
  setTaskOpen,
  statuses,
  allUsers,
  canEdit
}) => {
  const dispatch = useAppDispatch()

  const { emails } = useAppSelector(selectExtensionAPI)

  const { loaded: tasksLoaded, loadTask, tasks, updateTask } = useTaskAPI()
  const { setIDs } = useTaskList()
  const { tasks: ownerTasks } = useOwner()
  const { loadUser } = useUserAPI()

  const [savedContent, setSavedContent] = React.useState('')
  const [fullTask, setFullTask] = React.useState(null)
  const [showAsignee, setShowAsignee] = React.useState(false)
  const [showAddAsigneeModal, setShowAddAsigneeModal] = React.useState(false)
  const calenderRef = React.useRef(null)
  const anchorEl = React.useRef(null)
  const { id, session_ids, owner_id } = task

  const classes = useStyles({ canEdit })

  React.useEffect(() => {
    if (task) {
      const getTask = tasks[task.id]
      if (getTask) setFullTask(formatTaskForModal(getTask))
    }
  }, [task, tasks])

  // Gets the full task
  React.useEffect(() => {
    if (!tasksLoaded || fullTask) {
      return
    }

    const foundTask = tasks[task.id]
    if (foundTask) {
      if (Object.keys(foundTask).length > 2) {
        const formatted = formatTaskForModal(foundTask)
        setFullTask(formatted)
      }
    } else {
      loadTask(task.id)
    }
  }, [tasksLoaded, fullTask, task.id, loadTask, tasks])

  const handleOnClick = React.useCallback(() => {
    setTaskOpen(task?.id)
    setModalOpen(true)
    setIDs(ownerTasks, task?.id)
  }, [ownerTasks, setIDs, setModalOpen, setTaskOpen, task?.id])

  const hideAsigneePopper = React.useCallback(() => {
    setShowAsignee(false)
  }, [])

  const openAddAsigneeModal = React.useCallback(() => {
    setShowAsignee(false)
    setShowAddAsigneeModal(true)
  }, [])

  const hideAddAsigneeModal = React.useCallback(() => {
    setShowAddAsigneeModal(false)
  }, [])

  const handleUpload = React.useCallback(
    async data => {
      setSavedContent(data)

      try {
        const uploadId = data.id ?? id

        const toUpload = formatTaskForUpload({
          data,
          tasks,
          uploadId,
          id,
          owner_id,
          session_ids
        })

        if (JSON.stringify(data) !== JSON.stringify(savedContent)) {
          await updateTask(uploadId, toUpload)
        }
      } catch (err) {
        // error happened, reset content
        setSavedContent('')
        console.error('update error in TimelineTask', err)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [id, owner_id, savedContent, session_ids, tasks, updateTask, dispatch]
  )

  const handleRemoveAsignee = React.useCallback(
    id => {
      const newAsignees = [...fullTask.primary].filter(p => p !== id)
      setFullTask(vals => {
        const newVals = { ...vals, primary: newAsignees }
        handleUpload(newVals)
        return newVals
      })
    },
    [fullTask, handleUpload]
  )

  const handleAddAsignee = React.useCallback(
    ids => {
      const newAsignees = uniqueArray(fullTask.primary, ids)
      setFullTask(vals => {
        const newVals = { ...vals, primary: newAsignees }
        handleUpload(newVals)
        return newVals
      })
    },
    [fullTask, handleUpload]
  )

  const handleChange = prop => e => {
    let val
    if (prop === 'due_date') {
      val = e.toDate()
    } else {
      val = e.target.value
    }

    setFullTask(vals => {
      const newVals = { ...vals, [prop]: val }
      if (['status', 'due_date', 'assignee_ids'].includes(prop)) {
        handleUpload(newVals)
      }
      return newVals
    })
  }

  const handleEmailDrop = React.useCallback(
    async ({ id }) => {
      try {
        // Upload the email as a file
        const email = emails[id]
        const resp = await uploadFileTask(email.file)

        setFullTask(vals => {
          // Add the media id to the task
          const newVals = {
            ...vals,
            media_ids: [...(vals.media_ids ?? []), resp.id]
          }

          // Save and notify
          handleUpload(newVals)
          dispatch(AddSuccessAlert('Email added to task'))

          return newVals
        })
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
      }
    },
    [emails, handleUpload, dispatch]
  )

  const [{ isOver, isDragging }, dropRef] = useDrop({
    accept: EMAIL_DRAG_TYPE,
    drop: handleEmailDrop,
    collect: monitor => ({
      isOver: !!monitor.isOver(),
      isDragging: monitor.getItemType() === EMAIL_DRAG_TYPE
    })
  })

  const numberOfAssignees = fullTask?.primary?.length || 0
  //show max 3 asignees
  const asigneesToRender =
    numberOfAssignees > 0 ? [...fullTask?.primary].slice(0, 3) : []

  return (
    <React.Fragment>
      <div ref={dropRef} className={classes.root}>
        {(isOver || isDragging) && (
          <EmailDrop center isOver={isOver} text='task' />
        )}

        <div onClick={handleOnClick} className={classes.title}>
          {fullTask?.title || 'Loading...'}
        </div>

        <div className={classes.row}>
          <label className={classes.label} ref={anchorEl}>
            Assigned to
          </label>
          <div className={classes.assigneesRoot}>
            {numberOfAssignees > 0 ? (
              <>
                {asigneesToRender.map((id, index) => (
                  <TaskAssignee
                    key={id}
                    userId={id}
                    index={index}
                    classes={classes}
                    numberOfAssignees={numberOfAssignees}
                    allUsers={allUsers}
                    loadUser={loadUser}
                  />
                ))}
                {numberOfAssignees > 3 && (
                  <span className={classes.additionalAsignees}>{`${
                    numberOfAssignees - 3
                  }+`}</span>
                )}

                {canEdit && (
                  <span>
                    <DownArrowSmallIcon
                      onClick={() => setShowAsignee(true)}
                      className={classes.asigneeDetailsIcon}
                    />
                  </span>
                )}
              </>
            ) : (
              <>
                <label className={classes.data}>Unassigned</label>
                {canEdit && (
                  <span>
                    <DownArrowSmallIcon
                      onClick={() => setShowAsignee(true)}
                      className={classes.asigneeDetailsIcon}
                    />
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        {fullTask?.due_date && (
          <div className={classes.row}>
            <label className={classes.label}>Due date</label>
            <div className={classes.data} ref={calenderRef}>
              <Calendar
                value={fullTask?.due_date}
                onChange={handleChange('due_date')}
                anchorEl={calenderRef}
                disabled={!canEdit}
              >
                <div className={classes.dateRoot}>
                  <label>{formatDateRelative(fullTask?.due_date, true)}</label>
                  <TaskDueDate style={{ marginLeft: 8 }} />
                </div>
              </Calendar>
            </div>
          </div>
        )}
        <div className={classes.row}>
          <label className={classes.label}>Status</label>
          <div className={classes.data}>
            <DropdownV2
              disabled={!canEdit}
              options={statuses}
              value={statuses.find(status => status.id === fullTask?.status)}
              onSelect={val =>
                handleChange('status')({ target: { value: val.id } })
              }
              renderOption={statuses => statuses.name}
            />
          </div>
        </div>
      </div>
      {showAsignee && (
        <ClickAwayListener onClickAway={hideAsigneePopper}>
          <Popper
            open={showAsignee}
            disablePortal={false}
            anchorEl={anchorEl?.current}
            style={{ zIndex: '1500' }}
            placement='bottom-start'
          >
            <div className={classes.asigneeList}>
              {fullTask?.primary.length > 0 ? (
                fullTask?.primary.map(id => (
                  <AsigneeListRow
                    key={id}
                    userId={id}
                    handleRemoveAsignee={handleRemoveAsignee}
                  />
                ))
              ) : (
                <label className={classes.unnassignedLabel}>Unassigned</label>
              )}
              <div className={classes.asigneeListActions}>
                <button
                  className={classes.addAssigneeBtn}
                  onClick={openAddAsigneeModal}
                >
                  + Add
                </button>
                <button
                  className={classes.CloseAddAssigneeBtn}
                  onClick={hideAsigneePopper}
                >
                  Close
                </button>
              </div>
            </div>
          </Popper>
        </ClickAwayListener>
      )}
      {showAddAsigneeModal && (
        <AddAsigneeModal
          open={showAddAsigneeModal}
          onClose={hideAddAsigneeModal}
          addAsignee={handleAddAsignee}
          allUsers={allUsers}
        />
      )}
    </React.Fragment>
  )
}

export default Task
